/* Variant changes preserve the native product form and its personalization fields. */
if (!customElements.get('butterfly-product')) {
  customElements.define('butterfly-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('.bu-native-product-form');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.quantity = this.querySelector('[data-quantity]');
      this.gallery = this.querySelector('[data-gallery-image]');
      this.selectedThumb = this.querySelector('[data-selected-media]');
      this.dialog = this.querySelector('[data-image-dialog]');
      this.variants = new Map(JSON.parse(this.querySelector('[data-product-variants]').textContent).variants.map(v => [String(v.id), v]));
      this.selected = this.variants.get(this.variantInput.value);
      this.querySelectorAll('[hidden][data-gallery-src], [data-selected-media], [data-enlarge], [data-quantity-change]').forEach(el => { el.hidden = false; });
      this.filterThumbnails();
      this.addEventListener('click', event => {
        const choice = event.target.closest('[data-variant-choice]');
        if (choice && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault(); this.chooseVariant(choice.dataset.variantChoice); return;
        }
        const thumbnail = event.target.closest('[data-gallery-src], [data-selected-media]');
        if (thumbnail) { this.showThumbnail(thumbnail); return; }
        const step = event.target.closest('[data-quantity-change]');
        if (step) {
          try { Number(step.dataset.quantityChange) > 0 ? this.quantity.stepUp() : this.quantity.stepDown(); } catch (_) { this.quantity.value = this.quantity.min || 1; }
          this.quantity.dispatchEvent(new Event('change', { bubbles: true })); return;
        }
        if (event.target.closest('[data-enlarge]')) {
          this.querySelector('[data-dialog-image]').innerHTML = this.gallery.innerHTML;
          this.dialog.showModal(); return;
        }
        if (event.target.closest('[data-close-dialog]') || event.target === this.dialog) { this.dialog.close(); return; }
        if (event.target.closest('[data-remove-photo]')) { this.clearPhoto(); return; }
        const sizeLink = event.target.closest('.bu-size-link');
        if (sizeLink) {
          const guide = this.querySelector(sizeLink.getAttribute('href'));
          if (guide) { guide.open = true; }
        }
      });
      this.photoInput = this.querySelector('[data-photo-input]');
      this.photoInput?.addEventListener('change', () => this.showPhoto());
      this.restorePurchaseButtons = () => {
        this.querySelectorAll('[data-add-button], [data-mobile-add]').forEach(button => { button.disabled = !this.selected?.available; button.removeAttribute('aria-busy'); });
        this.querySelector('[data-add-label]').textContent = this.selected?.available ? 'Add to cart' : 'Unavailable';
      };
      window.addEventListener('pageshow', this.restorePurchaseButtons);
      this.form.addEventListener('submit', event => {
        if (!this.selected?.available || !this.form.reportValidity()) { event.preventDefault(); return; }
        this.querySelectorAll('[data-add-button], [data-mobile-add]').forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
        this.querySelector('[data-add-label]').textContent = 'Adding…';
      });
      if ('IntersectionObserver' in window) {
        this.purchaseObserver = new IntersectionObserver(entries => { this.querySelector('[data-mobile-buy]').hidden = entries[0].isIntersecting; }, { threshold: 0.1 });
        this.purchaseObserver.observe(this.querySelector('.bu-buy-row'));
      }
    }
    chooseVariant(id) {
      const variant = this.variants.get(String(id));
      if (!variant) return;
      this.selected = variant; this.variantInput.value = variant.id;
      let choice;
      this.querySelectorAll('[data-choice]').forEach(card => {
        const active = card.dataset.choice === String(id);
        card.classList.toggle('bu-choice--selected', active);
        const link = card.querySelector('[data-variant-choice]');
        if (active) { link.setAttribute('aria-current', 'true'); choice = link; } else link.removeAttribute('aria-current');
      });
      const label = choice.dataset.variantLabel;
      const contents = choice.dataset.variantContents;
      this.querySelector('[data-product-price]').textContent = variant.price;
      this.querySelector('[data-add-price]').textContent = variant.shortPrice;
      this.querySelector('[data-mobile-price]').textContent = variant.shortPrice;
      this.querySelector('[data-mobile-format]').textContent = label;
      this.querySelector('[data-selection-contents]').textContent = contents;
      this.querySelector('[data-add-label]').textContent = variant.available ? 'Add to cart' : 'Unavailable';
      this.querySelector('[data-mobile-add]').textContent = variant.available ? 'Add to cart' : 'Unavailable';
      this.querySelectorAll('[data-add-button], [data-mobile-add]').forEach(button => { button.disabled = !variant.available; button.removeAttribute('aria-busy'); });
      this.quantity.min = variant.min; this.quantity.step = variant.step;
      if (variant.max == null) this.quantity.removeAttribute('max'); else this.quantity.max = variant.max;
      let next = Math.max(variant.min, Number(this.quantity.value) || variant.min);
      next = variant.min + Math.ceil((next - variant.min) / variant.step) * variant.step;
      if (variant.max != null) next = Math.min(next, variant.max);
      this.quantity.value = next;
      this.gallery.innerHTML = variant.media;
      this.selectedThumb.innerHTML = variant.media;
      this.markThumbnail(this.selectedThumb);
      this.filterThumbnails();
      this.querySelector('[data-gallery-caption]').textContent = `${label} · ${contents}`;
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState(null, '', url);
      this.dispatchEvent(new CustomEvent('butterfly:variant-change', { bubbles: true, detail: { id: variant.id } }));
    }
    filterThumbnails() {
      const sku = this.selected?.sku;
      this.querySelectorAll('[data-gallery-src]').forEach(button => {
        const src = button.dataset.gallerySrc;
        button.hidden = Boolean(sku) && (!src.includes(sku + '_') || src.includes(sku + '_1.'));
      });
    }
    showThumbnail(button) {
      if (button.hasAttribute('data-selected-media')) {
        this.gallery.innerHTML = this.selected.media;
        const choice = this.querySelector(`[data-variant-choice="${this.selected.id}"]`);
        this.querySelector('[data-gallery-caption]').textContent = `${choice.dataset.variantLabel} · ${choice.dataset.variantContents}`;
      } else {
        const image = document.createElement('img'); image.src = button.dataset.gallerySrc; image.alt = button.dataset.galleryAlt || 'Product photograph';
        image.width = 1600; image.height = 1600; this.gallery.replaceChildren(image);
        this.querySelector('[data-gallery-caption]').textContent = image.alt;
      }
      this.markThumbnail(button);
    }
    markThumbnail(button) {
      this.querySelectorAll('.bu-thumbnail').forEach(thumb => { const active = thumb === button; thumb.classList.toggle('is-selected', active); thumb.setAttribute('aria-pressed', String(active)); });
    }
    showPhoto() {
      const file = this.photoInput.files[0];
      if (!file) { this.clearPhoto(); return; }
      const error = this.querySelector('[data-upload-error]');
      if (file.type && !['image/jpeg', 'image/png'].includes(file.type)) {
        this.clearPhoto(); error.textContent = 'Please choose a JPG or PNG photograph.'; error.hidden = false; return;
      }
      error.hidden = true;
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = URL.createObjectURL(file);
      this.querySelector('[data-photo-preview]').src = this.photoUrl;
      this.querySelector('[data-photo-filename]').textContent = file.name;
      this.querySelector('[data-upload-selected]').hidden = false;
      this.querySelector('[data-upload-title]').textContent = 'Change portrait photograph';
    }
    clearPhoto() {
      this.photoInput.value = '';
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = null;
      this.querySelector('[data-photo-preview]').removeAttribute('src');
      this.querySelector('[data-upload-selected]').hidden = true;
      this.querySelector('[data-upload-error]').hidden = true;
      this.querySelector('[data-upload-title]').textContent = 'Add a portrait photograph';
    }
    disconnectedCallback() { window.removeEventListener('pageshow', this.restorePurchaseButtons); this.purchaseObserver?.disconnect(); if (this.photoUrl) URL.revokeObjectURL(this.photoUrl); }
  });
}
